import { useMemo, useState, useEffect, useCallback } from "react";
import { useIntersectionObserver } from "usehooks-ts";
import {
  Badge,
  Icon,
  Button,
  Avatar,
  AvatarFallback,
  useToast,
} from "@elaniintc/goes-design-system";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/utils/theme";
import {
  useGetInsolvenciesInfinite,
  useRejectAppeal,
} from "@/queries/users/insolvencies.api";
import type {
  AppealDocumentType,
  InsolvencyType,
} from "@/types/entities/insolvencies";
import InsolvencyFormModal from "@/pages/users/external/details/actions/insolvency-form-modal";
import CancelInfractionModal from "./cancel-infraction-modal";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  getInsolvencyableName,
  getInsolvencyableId,
  getInsolvencyType,
  parseAmount,
  getPersonInitial,
  getDaysSinceCreated,
  getStatusConfig,
  getUnitText,
  convertUnitToDays,
} from "./insolvency.utils";

const renderSubjectAvatar = (
  item: InsolvencyType,
  size: "sm" | "md" = "sm",
) => {
  const type = getInsolvencyType(item);

  if (type === "personal") {
    return (
      <Avatar size={size} shape="circle">
        <AvatarFallback color="action">
          {getPersonInitial(getInsolvencyableName(item))}
        </AvatarFallback>
      </Avatar>
    );
  }

  return (
    <Avatar size={size} shape="circle">
      <AvatarFallback color="action" type="icon">
        <Icon type="fas" name="building" size="xs" />
      </AvatarFallback>
    </Avatar>
  );
};

export function InsolvencyListModal({
  isOpen,
  onClose,
  userId,
  userName,
  showActions = false,
}: {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
  showActions?: boolean;
}) {
  const { showToast } = useToast();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isRejectAppealOpen, setIsRejectAppealOpen] = useState(false);
  const [rejectAppealComment, setRejectAppealComment] = useState("");

  const {
    data: insolvenciesResponse,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useGetInsolvenciesInfinite(userId);

  const data = useMemo(
    () => insolvenciesResponse?.pages.flatMap((page) => page.data) ?? [],
    [insolvenciesResponse],
  );

  // Refetch data when modal opens
  useEffect(() => {
    if (isOpen) {
      refetch();
    }
  }, [isOpen, refetch]);

  // Intersection observer for infinite scroll
  const [sentinelRef, isIntersecting] = useIntersectionObserver({
    threshold: 0,
  });

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    if (isIntersecting) {
      handleLoadMore();
    }
  }, [isIntersecting, handleLoadMore]);

  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  const selected = useMemo(
    () => data.find((infraction) => infraction.id === selectedId) ?? data[0],
    [data, selectedId],
  );

  useEffect(() => {
    if (data.length > 0 && !selectedId) {
      setSelectedId(data[0].id);
    }
  }, [data, selectedId]);

  const statusConfig = selected ? getStatusConfig(selected) : null;
  // Calculate if the selected infraction is overdue
  const isOverdue = selected
    ? getDaysSinceCreated(selected.created_at) >=
      convertUnitToDays(
        selected.payment_deadline_value,
        selected.payment_deadline_unit,
      )
    : false;

  const isAnularDisabled =
    !selected ||
    isOverdue ||
    !["ACTIVE", "APPEAL_SUBMITTED", "APPEAL_REJECTED"].includes(
      selected.status,
    );

  const rejectAppealMutation = useRejectAppeal(selected?.id ?? "");

  const handleRejectAppeal = async () => {
    if (!rejectAppealComment.trim()) return;
    try {
      await rejectAppealMutation.mutateAsync({
        reason: selected?.reason ?? "",
        comments: rejectAppealComment,
      });
      showToast({
        title: "Apelación rechazada",
        description: "La apelación ha sido marcada como inválida.",
        options: { type: "success", duration: 5000 },
      });
      setIsRejectAppealOpen(false);
      setRejectAppealComment("");
      refetch();
    } catch {
      showToast({
        title: "Error al rechazar apelación",
        description: "No se pudo rechazar la apelación. Inténtalo de nuevo.",
        options: { type: "error", duration: 5000 },
      });
    }
  };

  // Filter comments into two arrays
  const otherComments =
    selected?.comments?.filter(
      (comment) => comment.created_by !== userId && comment.created_by !== null,
    ) ?? [];

  const userComments =
    selected?.comments?.filter(
      (comment) => comment.created_by === userId || comment.created_by === null,
    ) ?? [];

  const handleDownload = async (document: AppealDocumentType) => {
    try {
      window.open(document.url, "_blank", "noopener,noreferrer");
      showToast({
        title: "Documento descargado",
        description: "El documento ha sido descargado correctamente.",
        options: {
          type: "success",
          duration: 5000,
        },
      });
    } catch {
      showToast({
        title: "Error al descargar documento",
        description: "No se pudo descargar el documento. Inténtalo de nuevo.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
    }
  };

  if (isLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent type="modal" className="max-w-3xl px-6">
          <div className="flex items-center justify-center py-8">
            <p className="text-body-quiet">Cargando infracciones...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent type="modal" className="max-w-3xl px-6">
          <div className="flex items-center justify-center py-8">
            <p className="text-danger">Error al cargar infracciones</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent type="modal" className="max-w-3xl px-6">
          <DialogHeader className="mb-3">
            <DialogTitle className="text-h5 font-semibold text-heading">
              Infracciones
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center py-8">
            <p className="text-body-quiet">No hay infracciones registradas</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!selected) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        type="modal"
        className="max-w-3xl p-0 gap-0"
        showCloseButton={false}
      >
        <DialogHeader className="py-4 px-8 flex-row w-full justify-between items-center">
          <DialogTitle className="text-h5 font-semibold text-heading">
            Infracciones
          </DialogTitle>
          <DialogDescription className="sr-only">
            Lista y detalle de infracciones
          </DialogDescription>
          <DialogPrimitive.Close asChild>
            <Button
              type="button"
              size="icon"
              variant="quiet"
              aria-label="Cerrar"
            >
              <Icon name="close" type="fas" />
            </Button>
          </DialogPrimitive.Close>
        </DialogHeader>
        <div className="grid md:grid-cols-2 grid-cols-1 border-t border-neutral-silent items-start">
          <div className="flex flex-col px-4 py-6 border-r border-neutral-silent col-span-1">
            <div className="overflow-y-auto max-h-[500px]">
              {data.map((item) => (
                <div key={item.id} className="mb-1">
                  <button
                    type="button"
                    onClick={() => setSelectedId(item.id)}
                    className={cn(
                      "group flex w-full items-center justify-between rounded-xl px-3 py-2 text-left transition-colors",
                      item.id === selectedId
                        ? "bg-surface-action-accent"
                        : "bg-surface hover:border-neutral-quiet hover:bg-neutral-object-accent",
                    )}
                  >
                    <span className="flex items-center gap-3">
                      {renderSubjectAvatar(item)}
                      <span className="flex flex-col min-w-0">
                        <span
                          className={cn(
                            "text-body font-medium max-w-[210px] truncate",
                            item.id === selectedId
                              ? "text-action"
                              : "text-body-quiet",
                          )}
                        >
                          {item.reason}
                          {item.reference && (
                            <span className="font-normal text-xs ml-1 opacity-70">
                              ({item.reference})
                            </span>
                          )}
                        </span>
                      </span>
                    </span>

                    <span
                      className={cn(
                        "text-body-quiet text-right",
                        item.id === selectedId
                          ? "text-action"
                          : "text-body-quiet",
                      )}
                    >
                      ${parseAmount(item.amount).toFixed(2)}
                    </span>
                  </button>
                </div>
              ))}
              {/* Sentinel element for infinite scroll */}
              {hasNextPage && <div ref={sentinelRef} className="py-2" />}
              {isFetchingNextPage && (
                <div className="flex justify-center py-2">
                  <Icon
                    type="far"
                    name="spinner-third"
                    className="animate-spin text-on-disabled"
                  />
                </div>
              )}
            </div>
            {showActions && (
              <Button
                type="button"
                variant="tertiary"
                size="default"
                onClick={() => setIsAddModalOpen(true)}
                className="mt-2 w-full"
              >
                <Icon type="fas" name="plus" className="text-sm" size="sm" />
                Añadir infracción
              </Button>
            )}
          </div>
          <div
            className={cn(
              "flex flex-col col-span-1 py-6",
              showActions ? "pb-4" : "",
            )}
          >
            <div className="flex flex-col gap-4 overflow-y-auto max-h-[500px] px-4">
              <section className="rounded-lg border border-neutral-quiet bg-neutral-object-accent-silent py-3 px-4">
                <p className="my-1 text-body-loud font-semibold leading-2xl text-sm">
                  Información de infracción
                </p>
                <div className="flex flex-col gap-3">
                  <div className="flex items-start justify-between gap-2 rounded-lg border border-neutral-quiet bg-neutral-object px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <p className="text-body font-semibold">
                        {selected.reason}
                      </p>
                      <p className="text-sm text-body-quiet">
                        {selected.reference}
                      </p>
                    </div>
                    <Badge size="sm" variant="warning" fill="ghost">
                      ${parseAmount(selected.amount).toFixed(2)}
                    </Badge>
                  </div>
                  <div className="flex items-start justify-between gap-4 rounded-lg border border-neutral-quiet bg-neutral-object px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <p className="text-body font-semibold">
                        {selected.status === "SOLVENT" ||
                        selected.status === "CANCELLED"
                          ? "0"
                          : selected.payment_deadline_value?.toString()}{" "}
                        {getUnitText(selected.payment_deadline_unit)} para pago
                      </p>
                      <p className="text-sm text-body-quiet">
                        {selected.can_appeal
                          ? "Se puede apelar"
                          : "No se puede apelar"}
                      </p>
                    </div>
                    <Badge
                      size="sm"
                      variant={statusConfig?.badgeVariant}
                      fill="ghost"
                    >
                      {statusConfig?.badgeLabel}
                    </Badge>
                  </div>
                </div>
              </section>
              <section className="rounded-lg border border-neutral-quiet bg-neutral-object-accent-silent p-3">
                <p className="my-1 text-body-loud font-semibold leading-2xl text-sm">
                  Infracción asociada a
                </p>
                <div className="flex items-center gap-3 rounded-lg border border-neutral-quiet bg-neutral-object px-4 py-3">
                  {renderSubjectAvatar(selected, "md")}
                  <div>
                    <p className="text-body-loud font-semibold">
                      {getInsolvencyableName(selected)}
                    </p>
                    <p className="text-sm text-body-quiet">
                      {getInsolvencyableId(selected)}
                    </p>
                  </div>
                </div>
              </section>
              <section className="rounded-lg border border-neutral-quiet bg-neutral-object-accent-silent p-3">
                <p className="my-1 text-body-loud font-semibold leading-2xl text-sm">
                  Comentarios
                </p>
                <div className="rounded-lg py-1">
                  <div className="text-body">
                    {otherComments.length === 0
                      ? "Sin comentarios"
                      : otherComments.length === 1
                        ? otherComments[0].text
                        : otherComments.map((comment) => (
                            <div
                              key={comment.id}
                              className="text-sm text-body-quiet py-1"
                            >
                              - {comment.text}
                            </div>
                          ))}
                  </div>
                </div>
              </section>
              {selected.appeal_documents &&
                selected.appeal_documents.length > 0 && (
                  <section>
                    <p className="mb-3 text-h6 font-semibold text-heading">
                      Apelación del sancionado
                    </p>
                    <div className="rounded-lg border border-neutral-quiet bg-neutral-object-accent-silent p-3">
                      <p className="my-1 text-body-loud font-semibold leading-2xl text-sm">
                        Documentos de respaldo
                      </p>
                      <div className="flex flex-col gap-2 mt-2">
                        {selected.appeal_documents.map((doc) => (
                          <div
                            key={doc.id}
                            className="flex items-center justify-between rounded-lg border border-neutral-quiet bg-neutral-object px-4 py-3"
                          >
                            <div className="flex items-center gap-3">
                              <Icon
                                type="far"
                                name="file-alt"
                                className="text-sm text-body-quiet"
                              />
                              <span className="text-body text-sm truncate max-w-[120px]">
                                {doc.name}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-body-quiet text-xs border border-neutral-quiet rounded-full px-2 py-1">
                                {(doc.size / 1024).toFixed(0)} KB
                              </span>
                              <Button
                                variant="ghost"
                                size="icon"
                                type="button"
                                onClick={() => handleDownload(doc)}
                                className="text-action hover:text-action-loud transition-colors"
                                aria-label={`Descargar ${doc.name}`}
                              >
                                <Icon
                                  type="far"
                                  name="download"
                                  className="text-sm"
                                />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    {selected.status === "APPEAL_SUBMITTED" && showActions && (
                      <div className="mt-3 rounded-lg border border-neutral-quiet bg-neutral-object-accent-silent p-3">
                        {isRejectAppealOpen ? (
                          <div className="flex flex-col gap-2">
                            <p className="text-sm font-semibold text-body-loud">
                              Motivo del rechazo
                            </p>
                            <textarea
                              className="w-full rounded-lg border border-neutral-quiet bg-neutral-object px-3 py-2 text-sm text-body resize-none focus:outline-none focus:ring-1 focus:ring-action"
                              rows={3}
                              maxLength={500}
                              placeholder="Escribe el motivo por el cual la apelación es inválida..."
                              value={rejectAppealComment}
                              onChange={(e) =>
                                setRejectAppealComment(e.target.value)
                              }
                            />
                            <div className="flex justify-end gap-2 mt-1">
                              <Button
                                type="button"
                                variant="quiet"
                                size="sm"
                                onClick={() => {
                                  setIsRejectAppealOpen(false);
                                  setRejectAppealComment("");
                                }}
                              >
                                Cancelar
                              </Button>
                              <Button
                                type="button"
                                variant="danger-primary"
                                size="sm"
                                disabled={
                                  !rejectAppealComment.trim() ||
                                  rejectAppealMutation.isPending
                                }
                                onClick={handleRejectAppeal}
                              >
                                {rejectAppealMutation.isPending
                                  ? "Rechazando..."
                                  : "Confirmar rechazo"}
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <Button
                            type="button"
                            variant="danger-tertiary"
                            size="sm"
                            className="w-full"
                            onClick={() => setIsRejectAppealOpen(true)}
                          >
                            <Icon type="far" name="times-circle" />
                            Marcar apelación como inválida
                          </Button>
                        )}
                      </div>
                    )}
                  </section>
                )}
              {userComments.length > 0 && (
                <section className="rounded-lg border border-neutral-quiet bg-neutral-object-accent-silent p-3">
                  <p className="my-1 text-body-loud font-semibold leading-2xl text-sm">
                    Comentarios
                  </p>
                  <div className="rounded-lg py-1">
                    <div className="text-body">
                      {userComments.length === 1
                        ? userComments[0].text
                        : userComments.map((comment) => (
                            <div
                              key={comment.id}
                              className="text-sm text-body-quiet py-1"
                            >
                              - {comment.text}
                            </div>
                          ))}
                    </div>
                  </div>
                </section>
              )}
            </div>
            {showActions && (
              <section className="flex justify-end items-center gap-3 border-t border-neutral-quiet mt-4 pt-4 px-4">
                <Button
                  type="button"
                  variant="danger-tertiary"
                  size="default"
                  disabled={isAnularDisabled}
                  onClick={() => setIsCancelModalOpen(true)}
                >
                  Anular infracción
                </Button>
                <Button
                  type="button"
                  variant="default"
                  size="default"
                  onClick={() => setIsEditModalOpen(true)}
                >
                  Editar
                </Button>
              </section>
            )}
          </div>
        </div>
      </DialogContent>
      <InsolvencyFormModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          refetch();
        }}
        userId={userId}
        userName={userName}
      />
      <InsolvencyFormModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          refetch();
        }}
        userId={userId}
        userName={userName}
        mode="edit"
        insolvencyData={selected}
      />
      <CancelInfractionModal
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        infractionId={selected?.id || null}
        reason={selected?.reason || ""}
      />
    </Dialog>
  );
}

export default InsolvencyListModal;
